import React, { useState, useEffect } from 'react';
import { useUserData } from '../contexts/UserDataContext';
import { useLanguageToggle } from '../contexts/LanguageToggleContext';
import { useAuth } from '../contexts/AuthContext';
import ItemCard from './ItemCard';
import { api } from '../services/api';
import { ProgressBar } from './UserDashboard'; // Use named import
import { translations } from '../translations';

const languages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिंदी' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી' },
];

const sentenceGroups = [
  { label: '1 Word', key: '1', count: 200 },
  { label: '2 Words', key: '2', count: 500 },
  { label: '3 Words', key: '3', count: 450 },
  { label: '4 Words', key: '4', count: 500 },
  { label: '5 Words', key: '5', count: 500 },
  { label: '6+ Words', key: '6+', count: 1000 },
];

type Sentence = {
  en: string;
  hi: string;
  bn: string;
  mr: string;
  te: string;
  ta: string;
  gu: string;
  [key: string]: string;
};

// Sample/mock data for each group with all supported languages
const sampleSentences: Record<string, Sentence[]> = {
  '1': [
    { en: 'Go.', hi: 'जाओ।', bn: 'যাও।', mr: 'जा.', te: 'వెళ్ళు.', ta: 'போ.', gu: 'જાઓ.' },
    { en: 'Run.', hi: 'दौड़ो।', bn: 'দৌড়াও।', mr: 'धाव.', te: 'పరిగెత్తు.', ta: 'ஓடு.', gu: 'દોડો.' },
    { en: 'Sit.', hi: 'बैठो।', bn: 'বসো।', mr: 'बस.', te: 'కూర్చో.', ta: 'உட்கார்.', gu: 'બેસો.' },
    { en: 'Wait.', hi: 'रुको।', bn: 'অপেক্ষা করো।', mr: 'थांब.', te: 'ఆగు.', ta: 'காத்திரு.', gu: 'રોકો.' },
  ],
  '2': [
    { en: 'Come here.', hi: 'यहाँ आओ।', bn: 'এখানে আসো।', mr: 'इथे ये.', te: 'ఇక్కడ రా.', ta: 'இங்கே வா.', gu: 'અહીં આવો.' },
    { en: 'Stand up.', hi: 'खड़े हो जाओ।', bn: 'দাঁড়াও।', mr: 'उभे रहा.', te: 'లేచి నిల్చో.', ta: 'நில்.', gu: 'ઉભા રહો.' },
    { en: 'Speak up.', hi: 'जोर से बोलो।', bn: 'জোরে বলো।', mr: 'मोठ्याने बोल.', te: 'జోరుగా చెప్పు.', ta: 'உச்சரிக்கவும்.', gu: 'જોરથી બોલો.' },
    { en: 'Look here.', hi: 'यहाँ देखो।', bn: 'এখানে দেখো।', mr: 'इथे बघ.', te: 'ఇక్కడ చూడండి.', ta: 'இங்கே பாருங்கள்.', gu: 'અહીં જુઓ.' },
  ],
  '3': [
    { en: 'Open the door.', hi: 'दरवाजा खोलो।', bn: 'দরজা খোলো।', mr: 'दरवाजा उघडा.', te: 'ద్వారం తీయండి.', ta: 'கதவை திற.', gu: 'દરવાજો ખોલો.' },
    { en: 'Close the window.', hi: 'खिड़की बंद करो।', bn: 'জানালা বন্ধ করো।', mr: 'खिडकी बंद करा.', te: 'కిటికీ మూసి వేయండి.', ta: 'ஜன்னலை மூடு.', gu: 'ખિડકી બંધ કરો.' },
    { en: 'Wash your hands.', hi: 'अपने हाथ धोओ।', bn: 'তোমার হাত ধুয়ে নাও।', mr: 'हात धुवा.', te: 'చేతులు కడుక్కో.', ta: 'கைகளை கழுவவும்.', gu: 'હાથ ધોવો.' },
    { en: 'Read this book.', hi: 'यह किताब पढ़ो।', bn: 'এই বইটি পড়ো।', mr: 'ही पुस्तक वाचा.', te: 'ఈ పుస్తకం చదవండి.', ta: 'இந்த புத்தகத்தை படிக்கவும்.', gu: 'આ પુસ્તક વાંચો.' },
  ],
  '4': [
    { en: 'Turn off the light.', hi: 'लाइट बंद कर दो।', bn: 'লাইট বন্ধ করো।', mr: 'लाईट बंद करा.', te: 'లైట్ ఆఫ్ చేయండి.', ta: 'விளக்கை அணை.', gu: 'લાઇટ બંધ કરો.' },
    { en: 'Pick up the phone.', hi: 'फोन उठाओ।', bn: 'ফোন ধরো।', mr: 'फोन उचला.', te: 'ఫోన్ తీసుకో.', ta: 'தொலைபேசியை எடு.', gu: 'ફોન ઉઠાવો.' },
    { en: 'Read the next chapter.', hi: 'अगला अध्याय पढ़ो।', bn: 'পরবর্তী অধ্যায় পড়ো।', mr: 'पुढील धडा वाचा.', te: 'తర్వాతి అధ్యాయాన్ని చదవండి.', ta: 'அடுத்த அதிகாரத்தை படி.', gu: 'આગળનું અધ્યાય વાંચો.' },
    { en: 'Wash the dishes now.', hi: 'अब बर्तन धोओ।', bn: 'এখন বাসন ধুয়ে ফেলো।', mr: 'आता भांडी धुवा.', te: 'ఇప్పుడు పాత్రలు కడగండి.', ta: 'இப்போது பாத்திரங்களை கழுவவும்.', gu: 'હવે વાસણો ધોવો.' },
  ],
  '5': [
    { en: 'Please bring me some water.', hi: 'कृपया मुझे थोड़ा पानी लाओ।', bn: 'আমাকে একটু জল দাও।', mr: 'मला थोडं पाणी आण.', te: 'దయచేసి నాకు కొంత నీరు తీసుకురా.', ta: 'தயவு செய்து எனக்கு தண்ணீர் கொண்டு வா.', gu: 'મહેરબાની કરીને મને థોడું પાણી લાવો.' },
    { en: 'Tell me your name.', hi: 'मुझे अपना नाम बताओ।', bn: 'তোমার নাম বলো।', mr: 'तुझे नाव सांग.', te: 'నీ పేరు చెప్పు.', ta: 'உன் பெயரை சொல்.', gu: 'તમારું નામ કહો.' },
    { en: 'Write your address here.', hi: 'अपना पता यहाँ लिखो।', bn: 'তোমার ঠিকানা এখানে লেখো।', mr: 'तुझा पत्ता इथे लिही.', te: 'నీ చిరునామా ఇక్కడ రాయండి.', ta: 'உங்கள் முகவரியை இங்கே எழுதவும்.', gu: 'તમારું સરનામું અહીં લખો.' },
    { en: 'Call me in the evening.', hi: 'शाम को मुझे कॉल करो।', bn: 'সন্ধ্যায় আমাকে ফোন করো।', mr: 'संध्याकाळी मला कॉल करा.', te: 'సాయంత్రం నాకు కాల్ చేయండి.', ta: 'மாலை எனக்கு அழைப்பு விடுங்கள்.', gu: 'સાંજે મને ફોન કરો.' },
  ],
  '6+': [
    { en: 'Can you help me with this homework?', hi: 'क्या आप मेरी इस होमवर्क में मदद कर सकते हैं?', bn: 'তুমি কি আমাকে এই হোমওয়ার্কে সাহায্য করতে পারো?', mr: 'तू मला या गृहपाठात मदत करू शकतोस का?', te: 'మీరు నాకు ఈ హోంవర్క్‌లో సహాయం చేయగలరా?', ta: 'இந்த வீட்டுப்பாடத்தில் எனக்கு உதவ முடியுமா?', gu: 'શું તમે મને આ હોમવર્કમાં મદદ કરી શકો છો?' },
    { en: 'I am going to the market now.', hi: 'मैं अब बाजार जा रहा हूँ।', bn: 'আমি এখন বাজারে যাচ্ছি।', mr: 'मी आता बाजारात जात आहे.', te: 'నేను ఇప్పుడు మార్కెట్‌కు వెళ్తున్నాను.', ta: 'நான் இப்போது சந்தைக்கு போகிறேன்.', gu: 'હું હવે બજારમાં જઈ રહ્યો છું.' },
    { en: 'She wants to become a doctor.', hi: 'वह डॉक्टर बनना चाहती है।', bn: 'সে ডাক্তার হতে চায়।', mr: 'ती डॉक्टर व्हायची आहे.', te: 'ఆమె డాక్టర్ కావాలనుకుంటోంది.', ta: 'அவள் மருத்துவர் ஆக விரும்புகிறாள்.', gu: 'તે ડોક્ટર બનવા માંગે છે.' },
    { en: 'They are playing in the garden right now.', hi: 'वे अभी बगीचे में खेल रहे हैं।', bn: 'তারা এখন বাগানে খেলছে।', mr: 'ते आत्ता बागेत खेळत आहेत.', te: 'వారు ఇప్పుడు తోటలో ఆడుతున్నారు.', ta: 'அவர்கள் இப்போது தோட்டத்தில் விளையாடுகிறார்கள்.', gu: 'તેઓ હાલમાં બગીચામાં રમે છે.' },
  ],
};

interface SentencePracticePageProps {
  selectedLanguage?: string;
}

export default function SentencePracticePage({ selectedLanguage }: SentencePracticePageProps) {
  const { userData, addCompletedSentence, addMemorizedSentence, addFavoriteSentence, getCompletedSentences, refreshUserData } = useUserData();
  const actualSelectedLanguage = selectedLanguage || userData?.selectedLanguage || 'en';
  const [selectedGroup, setSelectedGroup] = useState(sentenceGroups[0].key);
  const [items, setItems] = useState<any[]>([]);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [completedIds, setCompletedIds] = useState<string[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const { currentUser } = useAuth();
  const userId = currentUser?.uid;
  const batchSize = 3;
  const [sentenceTotal, setSentenceTotal] = useState(0);
  const [sentenceCompleted, setSentenceCompleted] = useState(0);
  const [page, setPage] = useState(1); // Track current API page
  const [limit] = useState(50); // Always fetch 50 at a time
  const [totalPages, setTotalPages] = useState(1);
  const { showEnglish } = useLanguageToggle();
  const [loading, setLoading] = useState(false);
  const [isApiLoading, setIsApiLoading] = useState(false); // Add loading state to prevent duplicate calls
  const [loadingButtons, setLoadingButtons] = useState<{ [key: string]: boolean }>({});
  const [completedCount, setCompletedCount] = useState(0); // Track completed count for live updates

  // Add a function to check if a sentence is favorited
  const isSentenceFavorite = (item: any) => favoriteIds.includes(`sentence:${item._id}`);

  useEffect(() => {
    if (!userId || isApiLoading) return; // Prevent duplicate calls while loading

    console.log('SentencePracticePage: Fetching data for group:', selectedGroup, 'page:', page, 'userId:', userId);
    setItems([]); // Clear old data immediately
    setLoading(true);
    setIsApiLoading(true); // Set API loading state

    // Handle "6+" case - convert to 6 for API call
    const wordCount = selectedGroup === '6+' ? 6 : Number(selectedGroup);
    api.getMasterSentences(wordCount, userId, page, limit)
      .then(res => {
        console.log('SentencePracticePage: API response received for group:', selectedGroup);
        const items = res.items || [];
        setItems(items);
        setCurrentBatchIndex(0);
        setTotalCount(res.totalCount || 0);
        setTotalPages(res.totalPages || 1);

        // Auto-advance to next page if all items are completed/memorized
        if (items.length === 0 && page < (res.totalPages || 1)) {
          console.log('SentencePracticePage: All items completed on page', page, '- advancing to next page');
          setPage(page + 1);
        }
      })
      .catch(err => {
        console.error('Error fetching sentences:', err);
        setItems(sampleSentences[selectedGroup] || []);
        setCurrentBatchIndex(0);
      })
      .finally(() => {
        setLoading(false);
        setIsApiLoading(false); // Clear API loading state
      });
  }, [selectedGroup, userId, page, limit]);

  useEffect(() => {
    getCompletedSentences(Number(selectedGroup)).then(res => {
      const completed = res.map(s => s._id || '');
      setCompletedIds(completed);
      setCompletedCount(completed.length); // Set initial completed count
    });
    // Fetch favorite sentence IDs for this word count
    if (userId) {
      api.getFavoriteSentences(userId, selectedGroup === '6+' ? 6 : Number(selectedGroup)).then(favs => {
        setFavoriteIds(Array.isArray(favs) ? favs.map(f => f.itemId) : []);
      });
    }
  }, [selectedGroup, userId]);

  // Remove the unnecessary progress API call
  // useEffect(() => {
  //   if (!userId) return;
  //   api.getSentenceProgress(userId).then(res => {
  //     setSentenceTotal(res.total);
  //     setSentenceCompleted(res.completed);
  //   });
  // }, [userId]);

  const batchStart = currentBatchIndex * batchSize;
  const batchEnd = batchStart + batchSize;
  const currentBatch = items.slice(batchStart, batchEnd);

  const getTranslation = (item: any, language: string): string => {
    return item[language] || item.en;
  };

  const filteredSentences = currentBatch.filter(item => !completedIds.includes(item._id));
  const percent = sentenceTotal > 0 ? Math.round((sentenceCompleted / sentenceTotal) * 100) : 0;

  // Next/Prev button handlers for API pagination
  const handleNext = () => {
    if (batchEnd < items.length) {
      setCurrentBatchIndex(i => i + 1);
    } else if (page < totalPages) {
      setPage(p => p + 1);
      setCurrentBatchIndex(0);
    }
  };

  const handlePrev = () => {
    if (currentBatchIndex > 0) {
      setCurrentBatchIndex(i => i - 1);
    } else if (page > 1) {
      setPage(p => p - 1);
      setCurrentBatchIndex(Math.floor((limit / batchSize)) - 1); // last batch of previous page
    }
  };

  const handleDone = async (item: any) => {
    if (!item._id || loadingButtons[`done-${item._id}`]) return;

    // Set loading state for this specific button
    setLoadingButtons(prev => ({ ...prev, [`done-${item._id}`]: true }));

    // Optimistically update UI immediately
    setItems(prev => prev.filter(i => i._id !== item._id));
    setCompletedCount(prev => prev + 1); // Increment completed count immediately

    try {
      const wordCount = selectedGroup === '6+' ? 6 : (item.wordCount || 0);
      // Run API calls in parallel for better performance
      await Promise.all([
        addCompletedSentence(`sentence:${item._id}`, wordCount),
        api.memorizeSentence(userId!, `sentence:${item._id}`, wordCount, 'done')
      ]);
    } catch (error) {
      console.error('Error in handleDone:', error);
      // Revert optimistic update on error
      setItems(prev => [...prev, item]);
      setCompletedCount(prev => prev - 1); // Revert completed count on error
    } finally {
      setLoadingButtons(prev => ({ ...prev, [`done-${item._id}`]: false }));
    }
  };

  const handleMemorize = async (item: any) => {
    if (!item._id || loadingButtons[`memorize-${item._id}`]) return;

    // Set loading state for this specific button
    setLoadingButtons(prev => ({ ...prev, [`memorize-${item._id}`]: true }));

    // Optimistically update UI immediately
    setItems(prev => prev.filter(i => i._id !== item._id));
    setCompletedCount(prev => prev + 1); // Increment completed count immediately

    try {
      const wordCount = selectedGroup === '6+' ? 6 : (item.wordCount || Number(selectedGroup));
      await api.memorizeSentence(userId!, `sentence:${item._id}`, wordCount, 'memorized');
    } catch (error) {
      console.error('Error in handleMemorize:', error);
      // Revert optimistic update on error
      setItems(prev => [...prev, item]);
      setCompletedCount(prev => prev - 1); // Revert completed count on error
    } finally {
      setLoadingButtons(prev => ({ ...prev, [`memorize-${item._id}`]: false }));
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 sm:p-4 md:p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight drop-shadow-lg mb-4 md:mb-6 text-center">
          <span className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">Sentence Practice</span>
        </h1>

        {/* Group Selection - Responsive */}
        <div className="flex flex-wrap gap-1 sm:gap-2 justify-center mb-4 sm:mb-6 md:mb-8">
          {sentenceGroups.map((group) => (
            <button
              key={group.key}
              className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${selectedGroup === group.key ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-800 hover:bg-purple-200'}`}
              onClick={() => {
                setSelectedGroup(group.key);
                setPage(1);
                setCurrentBatchIndex(0);
              }}
            >
              {group.label}
            </button>
          ))}
        </div>

        {/* Navigation Buttons - Below Word Count Selection */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <div className="flex flex-row gap-2 items-center">
            <button
              onClick={() => window.location.href = '/memorize-sentences'}
              className="group relative px-3 sm:px-2 md:px-4 py-2 sm:py-1.5 md:py-2.5 bg-gradient-to-r from-purple-400 to-purple-500 text-white text-xs font-semibold rounded-full shadow-lg hover:from-purple-500 hover:to-purple-600 hover:shadow-purple-400/50 focus:ring-2 focus:ring-purple-300 transition-all duration-200 flex items-center justify-center hover:-translate-y-1 min-w-[80px] sm:min-w-[60px] md:min-w-[100px]"
            >
              <span className="hidden sm:inline">Memorize</span>
              <span className="sm:hidden">Memorize</span>
            </button>
            <button
              onClick={() => window.location.href = '/favorite-sentences'}
              className="group relative px-3 sm:px-2 md:px-4 py-2 sm:py-1.5 md:py-2.5 bg-gradient-to-r from-yellow-300 to-yellow-400 text-white text-xs font-semibold rounded-full shadow-lg hover:from-yellow-400 hover:to-yellow-500 hover:shadow-yellow-400/50 focus:ring-2 focus:ring-yellow-300 transition-all duration-200 flex items-center justify-center hover:-translate-y-1 min-w-[80px] sm:min-w-[60px] md:min-w-[100px]"
            >
              <span className="hidden sm:inline">Favorites</span>
              <span className="sm:hidden">Favorites</span>
            </button>
            <button
              onClick={() => window.location.href = '/completed-sentences'}
              className="group relative px-3 sm:px-2 md:px-4 py-2 sm:py-1.5 md:py-2.5 bg-gradient-to-r from-green-400 to-green-500 text-white text-xs font-semibold rounded-full shadow-lg hover:from-green-500 hover:to-green-600 hover:shadow-green-400/50 focus:ring-2 focus:ring-green-300 transition-all duration-200 flex items-center justify-center hover:-translate-y-1 min-w-[80px] sm:min-w-[60px] md:min-w-[100px]"
            >
              <span className="hidden sm:inline">Completed</span>
              <span className="sm:hidden">Completed</span>
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎉</div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
              Congratulations!
            </h3>
            <p className="text-gray-600 text-sm sm:text-base">
              You have completed all sentences for this word count.
            </p>
            <p className="text-gray-500 text-xs sm:text-sm mt-2">
              Great job! Keep practicing to maintain your skills.
            </p>
          </div>
        ) : (
          <div className="mb-8">
            {/* Count Display */}
            <div className="text-center mb-4">
              <p className="text-sm sm:text-base text-gray-600">
                Showing {completedCount} of {totalCount} sentences
              </p>
            </div>
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 justify-center items-stretch">
              {filteredSentences.map((item, idx) => {
                const favoriteButton = (
                  <button
                    className={`px-3 sm:px-4 md:px-6 py-2 rounded-full text-xs sm:text-sm bg-gradient-to-r from-yellow-400 to-yellow-600 text-white font-semibold shadow-lg hover:from-yellow-500 hover:to-yellow-700 hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-300 ${isSentenceFavorite(item) ? 'bg-yellow-500 text-white cursor-not-allowed opacity-60' : ''}`}
                    onClick={async () => {
                      if (isSentenceFavorite(item)) return;
                      if (currentUser?.uid && item._id) {
                        try {
                          // Optimistically update favoriteIds
                          setFavoriteIds(prev => [...prev, `sentence:${item._id}`]);
                          const wordCount = selectedGroup === '6+' ? 6 : Number(selectedGroup);
                          await api.addFavoriteSentence(currentUser.uid, `sentence:${item._id}`, wordCount);
                          await refreshUserData();
                          // Refresh favoriteIds after favoriting
                          api.getFavoriteSentences(currentUser.uid, wordCount).then(favs => {
                            setFavoriteIds(Array.isArray(favs) ? favs.map(f => f.itemId) : []);
                          });
                        } catch (error) {
                          console.error('Error favoriting sentence:', error);
                          // Revert optimistic update on error
                          setFavoriteIds(prev => prev.filter(id => id !== `sentence:${item._id}`));
                        }
                      }
                    }}
                    disabled={isSentenceFavorite(item)}
                    title={isSentenceFavorite(item) ? 'Already favorited' : 'Favorite'}
                  >
                    <span>⭐</span> <span className="hidden sm:inline">Favorite</span>
                  </button>
                );
                return (
                  <div key={item.en + idx} className="mb-4 w-full lg:w-1/3 bg-white rounded-xl shadow p-3 sm:p-4 flex flex-col gap-2">
                    <ItemCard
                      item={{
                        sentence: item.en,
                        en: item.en,
                        hi: item.hi,
                        bn: item.bn,
                        mr: item.mr,
                        te: item.te,
                        ta: item.ta,
                        gu: item.gu,
                        category: `${selectedGroup} words`
                      }}
                      type="sentence"
                      selectedLanguage={actualSelectedLanguage}
                      isFavorite={isSentenceFavorite(item)}
                      hideActions={true}
                      onDone={() => { }}
                      onMemorize={() => { }}
                      onFavorite={() => { }}
                    />
                    <div className="flex flex-wrap gap-1 sm:gap-2 mt-2 justify-center sm:justify-start">
                      <button
                        className="flex items-center gap-1 px-2 sm:px-3 md:px-6 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-green-400 to-green-600 text-white text-xs font-bold shadow-lg hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleDone(item)}
                        disabled={loadingButtons[`done-${item._id}`]}
                      >
                        {loadingButtons[`done-${item._id}`] ? (
                          <>
                            <span className="animate-spin text-xs">⏳</span>
                            <span className="hidden sm:inline text-xs">Processing...</span>
                          </>
                        ) : (
                          <>
                            <span className="text-xs">✅</span>
                            <span className="text-xs">Done</span>
                          </>
                        )}
                      </button>
                      <button
                        className="flex items-center gap-1 px-2 sm:px-3 md:px-6 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white text-xs font-bold shadow-lg hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-pink-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleMemorize(item)}
                        disabled={loadingButtons[`memorize-${item._id}`]}
                      >
                        {loadingButtons[`memorize-${item._id}`] ? (
                          <>
                            <span className="animate-spin text-xs">⏳</span>
                            <span className="hidden sm:inline text-xs">Processing...</span>
                          </>
                        ) : (
                          <>
                            <span className="text-xs">🧠</span>
                            <span className="text-xs">Memorize</span>
                          </>
                        )}
                      </button>
                      <button
                        className={`flex items-center gap-1 px-2 sm:px-3 md:px-6 py-1.5 sm:py-2 rounded-full text-xs font-bold shadow-lg hover:scale-105 hover:shadow-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-300 ${isSentenceFavorite(item) ? 'bg-yellow-500 text-white cursor-not-allowed opacity-60' : 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white hover:from-yellow-500 hover:to-yellow-700'}`}
                        onClick={async () => {
                          if (isSentenceFavorite(item)) return;
                          if (currentUser?.uid && item._id) {
                            try {
                              // Optimistically update favoriteIds
                              setFavoriteIds(prev => [...prev, `sentence:${item._id}`]);
                              const wordCount = selectedGroup === '6+' ? 6 : Number(selectedGroup);
                              await api.addFavoriteSentence(currentUser.uid, `sentence:${item._id}`, wordCount);
                              await refreshUserData();
                              // Refresh favoriteIds after favoriting
                              api.getFavoriteSentences(currentUser.uid, wordCount).then(favs => {
                                setFavoriteIds(Array.isArray(favs) ? favs.map(f => f.itemId) : []);
                              });
                            } catch (error) {
                              console.error('Error favoriting sentence:', error);
                              // Revert optimistic update on error
                              setFavoriteIds(prev => prev.filter(id => id !== `sentence:${item._id}`));
                            }
                          }
                        }}
                        disabled={isSentenceFavorite(item)}
                        title={isSentenceFavorite(item) ? 'Already favorited' : 'Favorite'}
                      >
                        <span className="text-xs">⭐</span>
                        <span className="text-xs">Favorite</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Navigation Controls - Responsive */}
        <div className="flex justify-center items-center gap-2 sm:gap-4 mt-6 sm:mt-8">
          <button
            onClick={handlePrev}
            disabled={currentBatchIndex === 0 && page === 1}
            className="px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 text-white text-xs sm:text-sm font-bold shadow-lg hover:from-blue-500 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            ← <span className="hidden sm:inline">Previous</span>
          </button>
          <button
            onClick={handleNext}
            disabled={currentBatchIndex === filteredSentences.length - 1 && page === totalPages}
            className="px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 text-white text-xs sm:text-sm font-bold shadow-lg hover:from-blue-500 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            <span className="hidden sm:inline">Next</span> →
          </button>
        </div>
      </div>
    </div>
  );
} 